/**
 * The control for light, dark, or follow-the-device.
 *
 * A three-way segmented control rather than a sun/moon switch: a two-state
 * switch cannot express "follow my phone", which is what most people
 * actually want and what this app does by default. Showing all three means
 * the current state is readable at a glance instead of inferred from which
 * icon is showing.
 */
import { Monitor, Moon, Sun } from "lucide-react";
import { useEffect, useState } from "react";

import {
  applyTheme,
  readTheme,
  storeTheme,
  watchSystemTheme,
  type Theme,
} from "../../lib/theme";

const OPTIONS: ReadonlyArray<{ value: Theme; label: string; Icon: typeof Sun }> = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  const [theme, setTheme] = useState<Theme>(() => readTheme());

  // While on "system", a change to the device preference has to repaint.
  // Without this the page keeps whatever it was when it loaded until the
  // next refresh.
  useEffect(() => {
    if (theme !== "system") return;
    return watchSystemTheme(() => applyTheme("system"));
  }, [theme]);

  function choose(next: Theme) {
    setTheme(next);
    applyTheme(next);
    storeTheme(next);
  }

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className="inline-flex rounded-lg border border-border bg-surface-2 p-0.5"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => choose(value)}
            className={[
              // 44px tall on touch, tighter where there is a pointer.
              "inline-flex min-h-11 items-center justify-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors sm:min-h-8",
              active
                ? "bg-surface text-ink shadow-sm"
                : "text-ink-3 hover:text-ink",
            ].join(" ")}
          >
            <Icon className="size-4" aria-hidden />
            {!compact && <span>{label}</span>}
          </button>
        );
      })}
    </div>
  );
}
